<template>
  <a-modal
    v-model:open="openLocal"
    :footer="null"
    :width="578"
    centered
    :maskClosable="true"
    class="hubspot-modal"
  >
    <!-- Header / contenido -->
    <div class="hubspot-modal__content">
      <!-- Logo -->
      <div class="hubspot-modal__logo">
        <HubspotLogo class="hs-select__icon" width="110" height="32" />
      </div>

      <CustomScrollbar class="hubspot-modal__scrollbar" height="calc(100vh - 260px)" auto>
        <!-- Título -->
        <a-typography-title :level="3" class="hubspot-modal__title">
          Conoce más sobre la importación y exportación de contactos
        </a-typography-title>

        <!-- Subtítulo -->
        <span class="hubspot-modal__subtitle">
          Al conectar la cuenta de HubSpot, los permisos están desactivados por defecto. 
          Antes de activarlos, ten en cuenta que solo se exportará o importarán las 
          <span class="bold">columnas homologadas con HubSpot</span> y el 
          <span class="bold">teléfono más reciente.</span>
          Para esto, desde el módulo 
          <a-button @click="goTemplates" class="template_link" type="link">
            Plantillas
            <IconExternalLink active style="margin: 0 4px"/>
          </a-button>
          se habilitó una columna de homologación con BeexCC en la plantilla de contactos 
          de todas las empresas y sus campañas.
        </span>

        <a-row class="hubspot-modal__grid">
          <div class="hubspot-modal__block">
            <h3 :level="5" class="hubspot-modal__blockTitle">
              Exportación de contactos de BeexCC a HubSpot
            </h3>
            <span class="hubspot-modal__blockText">
              Con esta opción podrás exportar contactos de BeexCC a HubSpot de forma individual o masiva, así como 
              actualizarlos en HubSpot cuando se editen desde BeexCC. Recuerda que la exportación será hacia 
              <span class="bold">una misma cuenta de HubSpot</span>. Podrás elegir entre los modos:
            </span>
          </div>

          <div class="hubspot-modal__block">
            <h3 :level="5" class="hubspot-modal__blockTitle">
              1. Modo manual
            </h3>
            <span class="hubspot-modal__blockText">
              Todo los contactos creados desde BeexCC se podrán exportar manualmente hacia HubSpot. Asimismo, 
              todo cambio realizado desde BeexCC al contacto exportado se podrá 
              <span class="bold">actualizar manualmente hacia HubSpot.</span>
            </span>
          </div>

          <div class="hubspot-modal__block">
            <h3 :level="5" class="hubspot-modal__blockTitle">
              2. Modo automático
            </h3>
            <span class="hubspot-modal__blockText">
              Todos los nuevos contactos creados desde BeexCC se exportarán automáticamente hacia HubSpot. 
              Esto <span class="bold">no incluye a los contactos existentes</span>, pero podrás crear eventos de exportación. 
              Asimismo, todo cambio realizado desde BeexCC al contacto exportado se 
              <span class="bold">actualizará automáticamente hacia HubSpot.</span>
            </span>
          </div>

          <a-alert
            class="hubspot-modal__note"
            type="info"
            show-icon
            message="En ambos modos será posible exportar los contactos existentes creados desde BeexCC a través de eventos de exportación."
          />
        </a-row>

        <a-row class="hubspot-modal__grid">
          <div class="hubspot-modal__block">
            <h3 :level="5" class="hubspot-modal__blockTitle">
              Importación de contactos de HubSpot a BeexCC
            </h3>
            <span class="hubspot-modal__blockText">
              Con esta opción podrás importar contactos de HubSpot a BeexCC de forma individual o masiva, así como 
              actualizarlos en BeexCC cuando se editen desde HubSpot. Recuerda que la importación será 
              <span class="bold">según la empresa y campaña</span>, 
              y los contactos deben cumplir con los siguientes criterios de importación de BeexCC:
              <ul>
                <li>
                  Tener completas las columnas Empresa y Campaña.
                </li>
                <li>
                  Tener completas las columnas homologadas marcadas como Dato requerido en las plantillas de contactos de BeexCC.
                </li>
              </ul>
              Podrás elegir entre los modos:
            </span>
          </div>

          <div class="hubspot-modal__block">
            <h3 :level="5" class="hubspot-modal__blockTitle">
              1. Modo manual
            </h3>
            <span class="hubspot-modal__blockText">
              Todo los contactos creados desde HubSpot que cumplan con los criterios de importación de BeexCC se podrán importar 
              manualmente hacia BeexCC. Asimismo, todo cambio realizado desde HubSpot al contacto importado se podrá 
              <span class="bold">actualizar manualmente hacia BeexCC.</span>
            </span>
          </div>

          <div class="hubspot-modal__block">
            <h3 :level="5" class="hubspot-modal__blockTitle">
              2. Modo automático
            </h3>
            <span class="hubspot-modal__blockText">
              Todos los nuevos contactos creados desde HubSpot que cumplan con los criterios de importación de BeexCC, se importarán 
              automáticamente hacia BeexCC. Esto <span class="bold">no incluye a los contactos existentes</span>, 
              pero podrás crear eventos de importación. Asimismo, todo cambio realizado desde HubSpot al contacto importado se 
              <span class="bold">actualizará automáticamente </span>hacia BeexCC.
            </span>
          </div>

          <a-alert
            class="hubspot-modal__note"
            type="info"
            show-icon
            message="En ambos modos será posible importar los contactos existentes creados desde HubSpot a través de eventos de importación."
          />
        </a-row>
      </CustomScrollbar>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { computed } from "vue";
import HubspotLogo from "@/app/integrations/components/icons/HubspotLogo.vue";
import IconExternalLink from "@/app/common/components/atoms/IconExternalLink.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import { useRouter } from "vue-router";

const props = defineProps<{
  open: boolean;
  noteText?: string;
}>();

const emit = defineEmits<{
  (e: "update:open", value: boolean): void;
}>();

const openLocal = computed({
  get: () => props.open,
  set: (v: boolean) => emit("update:open", v),
});

const router = useRouter();

const goTemplates = () => {
  router.push("/crm/templates");
};
</script>

<style scoped lang="sass">
:deep(.hubspot-modal .ant-modal-content)
  border-radius: 18px
  padding: 26px 26px 22px

:deep(.hubspot-modal .ant-modal-close)
  top: 14px
  right: 14px

.hubspot-modal
  &__content
    text-align: center
    padding: 30px 25px

  &__scrollbar
    padding-right: 10px

  &__logo
    display: flex
    justify-content: center
    margin-bottom: 10px

  &__title
    margin: 12px 0 !important
    font-weight: 600
    font-size: 24px
    color: #262626E0

  &__subtitle
    display: inline-block
    // max-width: 560px
    margin-bottom: 10px
    line-height: 1.45
    color: $gray-8

  &__grid
    margin-top: 10px
    margin-bottom: 16px
    text-align: left

  &__block
    display: flex
    flex-direction: column
    gap: 5px
    margin-bottom: 16px

  &__mock
    background: #fff
    /* border: 1px solid #f0f0f0 */
    /* border-radius: 12px */
    /* padding: 10px */
    display: flex
    justify-content: center
    align-items: center

  &__blockTitle
    margin: 0 !important
    font-weight: 600

  &__blockText
    line-height: 1.5
    color: $gray-8
    ul
      padding-inline-start: 26px
      margin-bottom: 20px

  /* Nota inferior tipo “card” */
  &__note
    text-align: left
    border-radius: 12px
    padding: 10px 14px

:deep(.hubspot-modal__mock img)
  width: 100%
  height: auto
  border-radius: 10px
  margin-bottom: 10px
  border: 1px solid $gray-4

.bold
  font-weight: 500

:deep(.hubspot-modal__note .ant-alert-message)
  line-height: 1.45
  color: #262626E0 !important

.template_link
  display: inline-flex
  align-items: center
  padding: 0
</style>
